import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { Popover, Modal, Select, Button } from "antd";
import { Icon } from "@iconify/react";
import { Button as AppButton } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";

const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function toDateInputValue(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}
function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}
function formatDate(date: string) {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(parsed);
}

interface CalendarDateFieldProps {
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  disablePast?: boolean;
  disableToday?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  autoExpand?: boolean;
  /** Opens the calendar in a centered dialog instead of an anchored popover. */
  openInModal?: boolean;
  /** Removes the trigger's own surface when it is rendered inside an existing field shell. */
  bare?: boolean;
}

export function CalendarDateField({
  value,
  placeholder = "Select date",
  onChange,
  disablePast = false,
  disableToday = false,
  open: openProp,
  onOpenChange,
  autoExpand = false,
  openInModal = false,
  bare = false,
}: CalendarDateFieldProps) {
  const today = startOfDay(new Date());
  // Current month, not next — disablePast means "today onward" is selectable, so today's
  // own month must stay browsable (and visible by default; see `baseDate` below).
  const earliestBrowsable = disablePast ? new Date(today.getFullYear(), today.getMonth(), 1) : null;
  const baseDate = value ? new Date(value) : earliestBrowsable ?? today;
  const initialDate = Number.isNaN(baseDate.getTime()) ? today : baseDate;

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = (next: boolean) => {
    onOpenChange?.(next);
    if (!isControlled) setInternalOpen(next);
  };

  const [viewMonth, setViewMonth] = useState(initialDate.getMonth());
  const [viewYear, setViewYear] = useState(initialDate.getFullYear());
  const selectedDate = value ? startOfDay(new Date(value)) : null;

  // antd's Popover auto-flips placement (top/bottom) when the trigger is near a
  // viewport edge, but it never clamps SIZE to what's actually available — on a
  // short screen it can flip to "open above" and still render the panel's top
  // off-screen with no way to scroll up to it, if neither direction alone has
  // enough room (verified: a 380px-tall viewport left the panel at top: -214px).
  // Compute the real available space ourselves, pick whichever side has more
  // room, and cap the panel's height to fit inside it — guarantees the panel is
  // always fully within the viewport, at the cost of an internal scrollbar on
  // genuinely cramped screens instead of unreachable content. Only applies to
  // the anchored Popover below; the Modal variants are already safe (centered,
  // with their own scrollable wrap).
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [panelLayout, setPanelLayout] = useState<{ placement: "bottomLeft" | "topLeft"; maxHeight?: number }>({
    placement: "bottomLeft",
  });
  const usesAnchoredPopover = !openInModal && !(autoExpand && !value);
  const POPOVER_EDGE_MARGIN = 16;
  useLayoutEffect(() => {
    if (!open || !usesAnchoredPopover || !triggerRef.current) return;
    const measure = () => {
      const rect = triggerRef.current!.getBoundingClientRect();
      const availableBelow = window.innerHeight - rect.bottom - POPOVER_EDGE_MARGIN;
      const availableAbove = rect.top - POPOVER_EDGE_MARGIN;
      setPanelLayout(
        availableBelow >= availableAbove
          ? { placement: "bottomLeft", maxHeight: Math.max(availableBelow, 0) }
          : { placement: "topLeft", maxHeight: Math.max(availableAbove, 0) },
      );
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open, usesAnchoredPopover]);

  // This component stays mounted across close/reopen (antd's Popover/Modal
  // don't unmount their content), so viewMonth/viewYear would otherwise stay
  // wherever the user last left them — reopening a field that already has a
  // value could show a different month than the selected date. Jump back to
  // the selected date's (or today's) month every time the calendar opens.
  useEffect(() => {
    if (!open) return;
    const jumpTo = value ? startOfDay(new Date(value)) : (earliestBrowsable ?? today);
    if (Number.isNaN(jumpTo.getTime())) return;
    setViewMonth(jumpTo.getMonth());
    setViewYear(jumpTo.getFullYear());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const years = earliestBrowsable
    ? Array.from({ length: 11 }, (_, i) => earliestBrowsable.getFullYear() + i)
    : Array.from({ length: 120 }, (_, i) => today.getFullYear() - i);
  const monthOptions = earliestBrowsable
    ? MONTH_LABELS.map((label, index) => ({ label, index })).filter(
        ({ index }) => viewYear > earliestBrowsable.getFullYear() || index >= earliestBrowsable.getMonth(),
      )
    : MONTH_LABELS.map((label, index) => ({ label, index }));
  const isPrevMonthDisabled = Boolean(
    earliestBrowsable && viewYear === earliestBrowsable.getFullYear() && viewMonth <= earliestBrowsable.getMonth(),
  );

  const firstDay = new Date(viewYear, viewMonth, 1);
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const prevMonthDays = new Date(viewYear, viewMonth, 0).getDate();
  const cells = Array.from({ length: 42 }, (_, index) => {
    const dayOffset = index - firstDay.getDay() + 1;
    const date = new Date(viewYear, viewMonth, dayOffset);
    const inCurrentMonth = dayOffset >= 1 && dayOffset <= daysInMonth;
    const label = inCurrentMonth ? dayOffset : dayOffset < 1 ? prevMonthDays + dayOffset : dayOffset - daysInMonth;
    return { date, label, inCurrentMonth };
  });

  const moveMonth = (direction: -1 | 1) => {
    if (direction === -1 && isPrevMonthDisabled) return;
    const next = new Date(viewYear, viewMonth + direction, 1);
    setViewMonth(next.getMonth());
    setViewYear(next.getFullYear());
  };
  const changeYear = (nextYear: number) => {
    setViewYear(nextYear);
    if (earliestBrowsable && nextYear === earliestBrowsable.getFullYear() && viewMonth < earliestBrowsable.getMonth()) {
      setViewMonth(earliestBrowsable.getMonth());
    }
  };

  // The Popover branch below already clamps+scrolls the OUTER chrome box via
  // `styles.container` — giving this inner div its own overflow/maxHeight on
  // top of that nests two independent scrollable regions over the same
  // content, which renders as two scrollbars stacked side by side. Only the
  // Modal branches need this div to size itself; they get no outer clamp.
  const calendarBodyStyle: CSSProperties = usesAnchoredPopover
    ? {}
    : { maxHeight: "calc(100vh - 2rem)", overflowY: "auto" };

  const calendarBody = (
    <div className="w-[312px] p-2" style={calendarBodyStyle}>
      <div className="mb-5 flex items-center justify-between gap-3">
        <Button
          shape="circle"
          type="text"
          disabled={isPrevMonthDisabled}
          onClick={() => moveMonth(-1)}
          icon={<Icon icon="lucide:chevron-left" className="h-5 w-5 text-[#7A7F8D]" />}
        />
        <Select
          value={viewMonth}
          onChange={setViewMonth}
          className="!h-10 !w-[110px]"
          options={monthOptions.map(({ label, index }) => ({ label, value: index }))}
        />
        <Select
          value={viewYear}
          onChange={changeYear}
          className="!h-10 !w-[90px]"
          options={years.map((y) => ({ label: y, value: y }))}
        />
        <Button
          shape="circle"
          type="text"
          onClick={() => moveMonth(1)}
          icon={<Icon icon="lucide:chevron-right" className="h-5 w-5 text-[#7A7F8D]" />}
        />
      </div>

      <div className="grid grid-cols-7 text-center text-sm text-[#7A7F86]">
        {["Su","Mo","Tu","We","Th","Fr","Sa"].map((d) => <div key={d} className="py-2">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xl">
        {cells.map(({ date, label, inCurrentMonth }) => {
          const normalized = startOfDay(date);
          const isDisabledDate = disablePast
            ? normalized < today
            : disableToday
              ? normalized >= today
              : normalized > today;
          const isSelected = selectedDate?.getTime() === normalized.getTime();
          return (
            <AppButton
              variant="ghost"
              key={`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`}
              disabled={!inCurrentMonth || isDisabledDate}
              onClick={() => { onChange(toDateInputValue(date)); setOpen(false); }}
              className={cn(
                "!h-11 !w-full !rounded-xl",
                !inCurrentMonth ? "!text-[#B8B8B8]" : "!text-[#222222] hover:!bg-[#F9DDE4]",
                isSelected ? "!bg-[#E4002B]/50 !text-white hover:!bg-[#E4002B]/50 hover:!text-white" : "",
                inCurrentMonth && !isSelected && date.getDay() === 0 ? "!bg-[#F9DDE4]" : "",
                isDisabledDate ? "!cursor-not-allowed !opacity-35 hover:!bg-transparent" : "",
              )}
            >
              {label}
            </AppButton>
          );
        })}
      </div>
    </div>
  );

  const trigger = (
    <AppButton
      variant="ghost"
      onClick={() => setOpen(!open)}
      className={cn(
        "!flex !h-auto !w-full !items-center !justify-between !gap-2 !text-left !text-sm",
        bare
          ? "!h-full !min-w-0 !flex-1 !bg-transparent !p-0"
          : "!h-[42px] !rounded-xl !border !border-[#E5E7EB] !bg-[#F9FAFB] !px-3 focus:!border-[#E4002B]/50 focus:!shadow-none",
      )}
    >
      <span className={value ? "text-[#0A2947]" : "text-[#7A7F8D]"}>
        {value ? formatDate(value) : placeholder}
      </span>
      <Icon icon="lucide:calendar-days" className="h-4 w-4 shrink-0 text-[#7A7F8D]" />
    </AppButton>
  );

  if (autoExpand && !value) {
    return (
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={360}
        centered
        className="[&_.ant-modal-content]:rounded-[22px] [&_.ant-modal-content]:p-6"
      >
        {calendarBody}
      </Modal>
    );
  }

  if (openInModal) {
    return (
      <>
        {trigger}
        <Modal
          open={open}
          onCancel={() => setOpen(false)}
          footer={null}
          width={360}
          centered
          className="[&_.ant-modal-content]:rounded-[22px] [&_.ant-modal-content]:p-6"
        >
          {calendarBody}
        </Modal>
      </>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement={panelLayout.placement}
      autoAdjustOverflow={false}
      content={calendarBody}
      overlayClassName="[&_.ant-popover-inner]:rounded-[22px] [&_.ant-popover-inner]:p-6 [&_.ant-popover-inner]:shadow-[0_18px_40px_rgba(17,24,39,0.18)]"
      // Caps the OUTER chrome box (.ant-popover-inner — the element the overlayClassName's
      // p-6 padding above lands on) to the real available space, not just our own inner
      // content div — otherwise antd's own padding/border sits outside our cap and still
      // pushes part of the panel off-screen (measured: ~38px of exactly that overhead).
      styles={panelLayout.maxHeight !== undefined ? { container: { maxHeight: panelLayout.maxHeight, overflowY: "auto" } } : undefined}
    >
      <span ref={triggerRef} className="block w-full">
        {trigger}
      </span>
    </Popover>
  );
}
